import Button from '@/components/base/button/button';
import PhosphorIcon from '@/components/base/icon/phosphor-icon';
import { Dropdown } from '@/features/navigation/components/desktop-navigation/dropdown';
import { HeaderNavigationLink } from '@/features/navigation/components/desktop-navigation/header-navigation-link';
import {
  NavigationStore,
  useNavigationStore,
} from '@/features/navigation/store/navigation-store';
import type { Navigation } from '@/features/navigation/types/navigation';
import { checkIfDeviceIsTouchScreen } from '@/features/navigation/utils/check-if-device-is-touch-screen';
import { EVENT_TIMEOUT_MS } from '@/features/navigation/utils/constants';
import { useRouter } from '@/navigation';
import { CaretCircleUp, CaretDown } from '@phosphor-icons/react';
import { clsx } from 'clsx';
import type { MouseEvent } from 'react';
import { useRef } from 'react';

interface Props {
  item: Navigation;
  depthLevel: number;
  className?: string;
}

const { activeDropdown, activeDropdownWithoutChildren, common, activeCommon } =
  {
    common: `h-clickable duration-0 border-3 border-[transparent] !px-xs before:absolute  before:-right-3  before:h-3 before:top-full before:w-3 before:bg-transparent before:content-[""]  after:absolute after:-left-3 after:top-3 after:h-3 after:top-full after:w-3 after:bg-transparent after:content-[""]`,
    activeCommon: `hover:outline-none rounded-t-s !rounded-b-none  border-3 !border-decorative before:!bg-decorative after:!bg-decorative`,
    activeDropdown: `!bg-main !border-b-white`,
    activeDropdownWithoutChildren: `!border-b-white !bg-main  !before:bg-decorative !after:bg-decorative`,
  };

const selector = ({
  categoryId,
  setCategoryId,
  resetAll,
  setSubcategoryId,
}: NavigationStore) => ({
  categoryId,
  setCategoryId,
  resetAll,
  setSubcategoryId,
});

export const HeaderNavigationItem = ({
  depthLevel,
  item,
  className,
  ...rest
}: Props) => {
  const { title, href, children } = item;
  const timeout = useRef<NodeJS.Timeout | null>(null);
  const { push } = useRouter();
  const { categoryId, setCategoryId, setSubcategoryId, resetAll } =
    useNavigationStore(selector);
  const isCurrentCategoryActive = categoryId === item.id;

  const isTouchScreenDevice = checkIfDeviceIsTouchScreen();

  const isAnyItemHaveChildren = item.children.some(
    (el) => el?.children?.length >= 1,
  );

  const onMouseEnter = () => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }

    timeout.current = setTimeout(() => {
      setCategoryId(item.id);
    }, EVENT_TIMEOUT_MS);
  };

  const onMouseLeave = () => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    timeout.current = setTimeout(() => {
      resetAll();
    }, EVENT_TIMEOUT_MS);
  };

  const onCategoryClick = ({ detail }: MouseEvent<HTMLButtonElement>) => {
    //detail 0 means that the event was triggered by keyboard
    if (isTouchScreenDevice || detail === 0) {
      setCategoryId(item.id);
      setSubcategoryId(null);
    } else if (href) {
      push(href);
    }
  };

  return children && children?.length >= 1 ? (
    <li
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={clsx(
        'relative',
        'w-max',
        isCurrentCategoryActive && 'z-10',
        className,
      )}
    >
      <Button
        className={clsx(
          common,
          isCurrentCategoryActive && activeCommon,
          isCurrentCategoryActive && isAnyItemHaveChildren
            ? activeDropdown
            : activeDropdownWithoutChildren,
        )}
        data-header-button-focus-id={item.id}
        variant="tertiary-no-frame"
        onClick={onCategoryClick}
        aria-expanded={isCurrentCategoryActive}
        {...rest}
      >
        <span
          className={clsx(
            'whitespace-nowrap text-nav-label',
            isCurrentCategoryActive ? 'text-strong' : 'text-default',
          )}
        >
          {title}
        </span>
        <PhosphorIcon
          Icon={isCurrentCategoryActive ? CaretCircleUp : CaretDown}
          variant="bold"
          className={clsx(
            'icon-m',
            isCurrentCategoryActive ? 'text-strong' : 'text-muted',
          )}
        />
        <span
          className={clsx(
            '-bottom-xxs absolute left-0 z-10 h-3 w-full bg-main',
            isCurrentCategoryActive ? 'flex' : 'hidden',
          )}
        />
      </Button>
      <Dropdown depthLevel={depthLevel} submenu={item} />
    </li>
  ) : (
    <HeaderNavigationLink
      href={href ?? '#'}
      title={title ?? ''}
      className={className}
    />
  );
};
